import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { X, AlertTriangle, Info, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Notice {
  id: string;
  title: string;
  body: string | null;
  severity: "info" | "warning" | "success" | "critical";
  audience: "all" | "global" | "regional";
  starts_at: string;
  ends_at: string | null;
}

const STYLES: Record<string, string> = {
  info: "bg-sky-50 border-sky-300 text-sky-900",
  warning: "bg-amber-50 border-amber-300 text-amber-900",
  success: "bg-emerald-50 border-emerald-300 text-emerald-900",
  critical: "bg-rose-50 border-rose-300 text-rose-900",
};
const ICONS: Record<string, any> = { info: Info, warning: AlertTriangle, success: CheckCircle2, critical: AlertCircle };

interface Props { role?: string }

export const AdminNoticesBanner = ({ role }: Props) => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem("admin_notice_dismissed") || "[]")); } catch { return new Set(); }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const now = new Date().toISOString();
      const { data } = await (supabase as any)
        .from("admin_notices")
        .select("id,title,body,severity,audience,starts_at,ends_at")
        .eq("is_active", true)
        .lte("starts_at", now)
        .or(`ends_at.is.null,ends_at.gt.${now}`)
        .order("starts_at", { ascending: false })
        .limit(5);
      if (cancelled) return;
      const filtered = (data || []).filter((n: Notice) => {
        if (n.audience === "all") return true;
        if (n.audience === "global") return role === "global_admin" || role === "president";
        if (n.audience === "regional") return role === "regional_admin";
        return true;
      });
      setNotices(filtered);
    })();
    return () => { cancelled = true; };
  }, [role]);

  const dismiss = (id: string) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    sessionStorage.setItem("admin_notice_dismissed", JSON.stringify([...next]));
  };

  const visible = notices.filter((n) => !dismissed.has(n.id));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-2 mb-4">
      {visible.map((n) => {
        const Icon = ICONS[n.severity] || Info;
        return (
          <div key={n.id} className={`rounded-md border px-3 py-2 flex items-start gap-3 ${STYLES[n.severity] || STYLES.info}`}>
            <Icon className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">{n.title}</p>
              {n.body && <p className="text-xs mt-0.5 whitespace-pre-wrap opacity-90">{n.body}</p>}
            </div>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => dismiss(n.id)}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        );
      })}
    </div>
  );
};

export default AdminNoticesBanner;
